import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { NAV_LINKS, LOGO, scrollToId } from "@/lib/site";
import { GlowButton } from "@/components/primitives";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 220 : 0);
  };

  return (
    <>
      <motion.header
        initial={{ y: -70, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
          scrolled ? "border-b border-slate-200/80 bg-white/85 backdrop-blur-xl" : "bg-transparent"
        }`}
        data-testid="main-navbar"
      >
        <nav className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3.5 sm:px-6 lg:px-8">
          <button
            onClick={() => go("home")}
            className="flex items-center gap-2.5"
            data-testid="nav-logo"
            aria-label="Digital Advyzer home"
          >
            <img
              src={LOGO}
              alt="Digital Advyzer logo"
              className="h-9 w-9 rounded-full object-cover ring-1 ring-slate-200"
              loading="eager"
            />
            <span className="font-display text-lg font-bold tracking-tight text-[#111827]">
              Digital Advyzer
            </span>
          </button>

          <div className="hidden items-center gap-1 lg:flex">
            {NAV_LINKS.map((link) => (
              <button
                key={link.id}
                onClick={() => go(link.id)}
                className="rounded-full px-3.5 py-2 text-sm font-medium text-slate-600 transition-colors hover:text-[#111827]"
                data-testid={`nav-link-${link.id}`}
              >
                {link.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <div className="hidden sm:block">
              <GlowButton onClick={() => go("contact")} className="!px-5 !py-2.5" data-testid="nav-cta-lets-talk">
                Let's Talk
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </GlowButton>
            </div>
            <button
              onClick={() => setOpen((v) => !v)}
              className="grid h-10 w-10 place-items-center rounded-full ring-1 ring-slate-200 text-[#111827] lg:hidden"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              data-testid="nav-mobile-toggle"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="absolute inset-0 bg-white/95 backdrop-blur-sm" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ y: -16, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -16, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-x-4 top-20 rounded-3xl bg-white p-5 shadow-float ring-1 ring-slate-100"
            >
              <div className="flex flex-col">
                {NAV_LINKS.map((link, i) => (
                  <motion.button
                    key={link.id}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 * i }}
                    onClick={() => go(link.id)}
                    className="border-b border-slate-100 py-4 text-left font-display text-xl font-semibold text-[#111827]"
                    data-testid={`mobile-nav-link-${link.id}`}
                  >
                    {link.label}
                  </motion.button>
                ))}
              </div>
              <GlowButton onClick={() => go("contact")} className="mt-5 w-full" data-testid="mobile-cta-lets-talk">
                Let's Talk
                <ArrowRight className="h-4 w-4" />
              </GlowButton>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
